import { Panel } from "../../../../components/panels/Panel";
import { Ergonomics5Risks } from "../Ergonomics5Risks";
import { useState, useEffect } from "react";
import { getInfluxdbData } from "../../../../services/api/influxdb-tools";
import { useOrgData } from "../../../../contexts/OrgDataContext";
import { REBA_THRESHOLDS_ADJUSTED } from "../../../../services/ergonomics/ergonomics-service";
import { useErgonomicsContext } from "../../../../contexts/ErgonomicsContext";
import { formatSecondsToFullDate, getCurrentTimestamp } from "../../../../helpers/date/date-helper";
import { InformationCircleIcon } from "@heroicons/react/20/solid";

export const ErgonomicsColoredScore = ({ score, type = "reba" }) => {
  if (score === 0)
    return <div className='text-2xl'>-</div>

  let colorClass = '';
  let roundedScore;
  if (type === "reba") {
    roundedScore = Math.round(score * 10) / 10;
    if (roundedScore >= REBA_THRESHOLDS_ADJUSTED.step_13[4]) colorClass = 'rgba(255, 120, 150, 0.9)'; // Bright red
    else if (roundedScore >= REBA_THRESHOLDS_ADJUSTED.step_13[3]) colorClass = 'rgba(255, 99, 132, 0.8)'; // Red
    else if (roundedScore >= REBA_THRESHOLDS_ADJUSTED.step_13[2]) colorClass = 'rgba(255, 150, 86, 0.8)'; // Orange
    else if (roundedScore >= REBA_THRESHOLDS_ADJUSTED.step_13[1]) colorClass = 'rgba(255, 206, 86, 0.8)'; // Yellow
    else colorClass = 'rgba(75, 255, 192, 0.8)'; // Green
  }
  else if (type === "index") {
    roundedScore = Math.round(score * 10) / 10;
    if (roundedScore >= 80) colorClass = "rgba(75, 255, 192, 0.8)";   // Green
    else if (roundedScore >= 60) colorClass = "rgba(255, 206, 86, 0.8)"; // Yellow
    else if (roundedScore >= 40) colorClass = "rgba(255, 150, 86, 0.8)"; // Orange
    else if (roundedScore >= 20) colorClass = "rgba(255, 99, 132, 0.8)"; // Red
    else colorClass = "rgba(255, 120, 150, 0.9)";                // Bright red
  }
  else {
    colorClass = 'white';
    roundedScore = score;
  }
  return <div className="text-2xl" style={{ color: colorClass}}>{roundedScore}</div>
}

export const ErgonomicsCardData = ({ forceLastDay = false }) => {
  const { workstations, workstationSelected } = useOrgData();
  const { selectedDateRange } = useErgonomicsContext();

  const stopDate = forceLastDay ? getCurrentTimestamp() : Math.floor(selectedDateRange.stop / 1000); // timestamp in ms
  const startDate = stopDate - 7 * 86400; // timestamp in ms

  const [rebaDailyMean, setRebaDailyMean] = useState(0);
  const [rebaShiftMean, setRebaShiftMean] = useState([0, 0]);
  const [rebaShiftRank, setRebaShiftRank] = useState(['-', '-']);
  const [riskDailyRepartitionPct, setRiskDailyRepartitionPct] = useState([0, 0, 0, 0, 0]);
  const [rebaDailyPeakHourDay, setRebaDailyPeakHourDay] = useState(null);
  const [rebaDailyPeakHour30dRisk, setRebaDailyPeakHour30dRisk] = useState([0, 0, 0, 0, 0]);
  const [rebaDailyPeakHourSameDay4wRisk, setRebaDailyPeakHourSameDay4wRisk] = useState([0, 0, 0, 0, 0]);

  useEffect(() => {
    const getData = async () => {
      let data;
      data = await getInfluxdbData('engine', 'results',
        [['client_id', '==', workstationSelected?.device?.stream_id]], ['reba_daily_mean'],
        startDate, stopDate, null,null, 'last');
      if (data.length) setRebaDailyMean(data[0].value);

      data = await getInfluxdbData('engine', 'results',
        [['client_id', '==', workstationSelected?.device?.stream_id]], ['reba_shift_mean'],
        startDate, stopDate, null,null, 'last', ['shift']);
      if (data.length) setRebaShiftMean([data.find(d => d.shift === 'morning').value, data.find(d => d.shift === 'afternoon').value]);

      data = await getInfluxdbData('engine', 'results',
        [], ['reba_shift_rank'],
        startDate, stopDate, null,null, 'last', ['client_id', 'shift']);
      if (data.length) {
        const morningStreamId = data.find(d => d.shift === 'morning' && d.value === 2).client_id
        const morningStationName = workstations.find(w => w.device?.stream_id === morningStreamId)?.name;
        const afternoonStreamId = data.find(d => d.shift === 'afternoon' && d.value === 2).client_id
        const afternoonStationName = workstations.find(w => w.device?.stream_id === afternoonStreamId)?.name;
        setRebaShiftRank([morningStationName, afternoonStationName]);
      }

      data = await getInfluxdbData('engine', 'results',
        [['client_id', '==', workstationSelected?.device?.stream_id]], ['reba_daily_peak_hour_of_day'],
        startDate, stopDate, null,null, 'last');
      if (data.length) setRebaDailyPeakHourDay(data[0].value);

      data = await getInfluxdbData('engine', 'results',
        [['client_id', '==', workstationSelected?.device?.stream_id]], ['risk_daily_repartition_pct'],
        startDate, stopDate, null,null, 'last', ['client_id', 'risk_code']);
      if (data.length) setRiskDailyRepartitionPct([
        Math.ceil(data.find(d => d.risk_code === '0').value),
        Math.ceil(data.find(d => d.risk_code === '1').value),
        Math.ceil(data.find(d => d.risk_code === '2').value),
        Math.ceil(data.find(d => d.risk_code === '3').value),
        Math.ceil(data.find(d => d.risk_code === '4').value),
      ]);

      data = await getInfluxdbData('engine', 'results',
        [['client_id', '==', workstationSelected?.device?.stream_id]], ['reba_daily_peak_hour_30d_pct_by_risk'],
        startDate, stopDate, null,null, 'last', ['client_id', 'risk_code']);
      if (data.length) setRebaDailyPeakHour30dRisk([
        Math.ceil(data.find(d => d.risk_code === '0').value),
        Math.ceil(data.find(d => d.risk_code === '1').value),
        Math.ceil(data.find(d => d.risk_code === '2').value),
        Math.ceil(data.find(d => d.risk_code === '3').value),
        Math.ceil(data.find(d => d.risk_code === '4').value),
      ]);

      data = await getInfluxdbData('engine', 'results',
        [['client_id', '==', workstationSelected?.device?.stream_id]], ['reba_daily_peak_hour_same_weekday_4w_pct_by_risk'],
        startDate, stopDate, null,null, 'last', ['client_id', 'risk_code']);
      if (data.length) setRebaDailyPeakHourSameDay4wRisk([
        Math.ceil(data.find(d => d.risk_code === '0').value),
        Math.ceil(data.find(d => d.risk_code === '1').value),
        Math.ceil(data.find(d => d.risk_code === '2').value),
        Math.ceil(data.find(d => d.risk_code === '3').value),
        Math.ceil(data.find(d => d.risk_code === '4').value),
      ]);
    };

    getData();
  }, [workstationSelected, selectedDateRange]);

  return (
    <Panel title="RESUME DES DONNEES D'HIER" subTitle={formatSecondsToFullDate(stopDate - 86400)}>
      <div className="grid grid-cols-3 grid-rows-3 gap-4">
        <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
          <ErgonomicsColoredScore score={rebaDailyMean}/>
          <div className="text-sm">Moyenne REBA journalière</div>
        </div>
        <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
          <ErgonomicsColoredScore score={rebaShiftMean[0]}/>
          <div className="text-sm">Moyenne REBA équipe matin</div>
        </div>
        <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
          <ErgonomicsColoredScore score={rebaShiftMean[1]}/>
          <div className="text-sm">Moyenne REBA équipe après-midi</div>
        </div>

        <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
          <div className="h-[70px]">
            <Ergonomics5Risks bins={riskDailyRepartitionPct}/>
          </div>
          <div className="text-sm">Répartition par niveau de risque du jour</div>
        </div>
        <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
          <div className="text-2xl text-white">{rebaShiftRank[0]}</div>
          <div className="text-sm">Station la plus exposée équipe matin</div>
        </div>
        <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
          <div className="text-2xl text-white">{rebaShiftRank[1]}</div>
          <div className="text-sm">Station la plus exposée équipe après-midi</div>
        </div>

        <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
          <div className="text-2xl text-white">
            {
              rebaDailyPeakHourDay !== null ?
                <>{`${rebaDailyPeakHourDay}h-${rebaDailyPeakHourDay + 1}h`}</> :
                <>Aucune heure critique</>
            }
          </div>
          <div className="text-sm">Heure la plus intense de la journée</div>
        </div>
        <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
          <div className="h-[70px]">
            <Ergonomics5Risks bins={rebaDailyPeakHour30dRisk}/>
          </div>
          <div className="text-sm">Répartition par niveau de risque, pour cette heure critique, dans les 30 derniers jours</div>
        </div>
        <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
          <div className="h-[70px]">
            <Ergonomics5Risks bins={rebaDailyPeakHourSameDay4wRisk}/>
          </div>
          <div className="text-sm">Répartition par niveau de risque, pour cette heure critique, le même jour, dans les 30 derniers jours</div>
        </div>
      </div>
      <div className="max-h-16 flex text-sm text-perception-gray-600 mt-4">
        <InformationCircleIcon aria-hidden="true" className="min-w-5 size-5 mr-2" />
        Données calculées automatiquement quotidiennement à 22h.
      </div>
    </Panel>
  );
};